import { performanceManager } from "./performance-manager.js";

let spaceAnimationFrame = null;
let isTransitioning = false;

// Space background variables (moved from space-background.js)
let spaceScene, spaceCamera, spaceRenderer, spaceStars, spaceStarGeo, spaceClock, spaceNebulaGroup, spaceHeader;
let spaceWarpFactor = 0.0;
let spaceCameraShaking = false;
let spaceShakeEndTime = 0;
let spaceManualWarpControl = false;
const spaceShakeDuration = 2.0;

function initBackgroundManager() {
  // Preload critical resources
  preloadCriticalResources();

  // Initialize space background
  initSpaceBackground();

  // Setup intersection observer for performance
  setupIntersectionObserver();
}

function preloadCriticalResources() {
  // Preload performance manager
  import("./performance-manager.js").then(() => {
    console.log("Performance manager loaded");
  });
}

function setupIntersectionObserver() {
  // Reduce animations when not visible to save battery
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.target.id === "bg") {
        if (!entry.isIntersecting) {
          // Reduce animation frequency when not visible
          console.log("Canvas not visible, reducing performance");
        }
      }
    });
  });

  const canvas = document.getElementById("bg");
  if (canvas) {
    observer.observe(canvas);
  }
}

function initSpaceBackground() {
  spaceHeader = document.querySelector("header");
  const canvas = document.getElementById("bg");
  if (!canvas) return;

  // Reset warp control state
  spaceWarpFactor = 0.01;
  spaceManualWarpControl = false;
  spaceCameraShaking = false;

  // Set initial warp slider value
  const warpSlider = document.getElementById("warp-slider");
  if (warpSlider) {
    warpSlider.value = spaceWarpFactor;
  }

  // Show warp controls for space background
  const warpControls = document.getElementById("warp-controls");
  if (warpControls) {
    warpControls.style.display = "flex";
    warpControls.classList.remove("manual-control"); // Reset to automatic mode
  }

  spaceScene = new THREE.Scene();
  spaceClock = new THREE.Clock();

  spaceCamera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 10000);
  spaceCamera.position.set(0, 0, 800);

  spaceRenderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: performanceManager.shouldUseAntialias(),
    alpha: true,
    powerPreference: "high-performance",
  });

  // Apply render scale for performance
  const renderScale = performanceManager.getRenderScale();
  const scaledWidth = Math.floor(window.innerWidth * renderScale);
  const scaledHeight = Math.floor(window.innerHeight * renderScale);
  spaceRenderer.setSize(scaledWidth, scaledHeight, false);

  // Scale canvas to fill screen
  if (renderScale < 1.0) {
    canvas.style.width = window.innerWidth + "px";
    canvas.style.height = window.innerHeight + "px";
  }
  spaceRenderer.setClearColor(0x000000, 1);

  createSpaceStars();
  createSpace3DNebula();
  setupSpaceEventListeners();
  animateSpace();
}

function createSpaceStars() {
  const starCount = performanceManager.getStarCount();
  const positions = new Float32Array(starCount * 6);
  const randoms = new Float32Array(starCount * 2);
  const opacities = new Float32Array(starCount * 2);

  for (let i = 0; i < starCount; i++) {
    const x = THREE.MathUtils.randFloatSpread(4000);
    const y = THREE.MathUtils.randFloatSpread(4000);
    const z = THREE.MathUtils.randFloatSpread(4000);

    positions[i * 6] = x;
    positions[i * 6 + 1] = y;
    positions[i * 6 + 2] = z;

    positions[i * 6 + 3] = x;
    positions[i * 6 + 4] = y;
    positions[i * 6 + 5] = z;

    const rand = Math.random();
    randoms[i * 2] = rand;
    randoms[i * 2 + 1] = rand;

    opacities[i * 2] = 1.0;
    opacities[i * 2 + 1] = 0.0;
  }

  spaceStarGeo = new THREE.BufferGeometry();
  spaceStarGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  spaceStarGeo.setAttribute("random", new THREE.BufferAttribute(randoms, 1));
  spaceStarGeo.setAttribute("opacity", new THREE.BufferAttribute(opacities, 1));

  const starMaterial = new THREE.ShaderMaterial({
    uniforms: {
      time: { value: 0 },
      warpFactor: { value: 0 },
    },
    vertexShader: `
      attribute float random;
      attribute float opacity;
      varying float vOpacity;
      uniform float time;
      uniform float warpFactor;

      void main() {
        vOpacity = opacity;
        
        vec3 p = position;
        
        float speed = 10.0 + random * 200.0 + warpFactor * random * 8000.0;
        p.z += mod(time * speed, 4000.0) - 2000.0;
        
        vec3 direction = normalize(vec3(p.x, p.y, 4000.0));
        float streakLength = (1.0 - opacity) * warpFactor * 500.0 * (0.5 + random * 0.5);
        p += direction * streakLength;
        
        gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
      }
    `,
    fragmentShader: `
      varying float vOpacity;
      uniform float warpFactor;

      void main() {
        float alpha = mix(1.0, vOpacity, warpFactor);
        gl_FragColor = vec4(1.0, 1.0, 1.0, alpha);
      }
    `,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });

  spaceStars = new THREE.LineSegments(spaceStarGeo, starMaterial);
  spaceScene.add(spaceStars);
}

function createSpace3DNebula() {
  spaceNebulaGroup = new THREE.Group();
  spaceNebulaGroup.position.set(400, 250, -100);

  const layers = performanceManager.getNebulaLayers();

  for (let i = 0; i < layers; i++) {
    const size = 400 + i * 100;
    const geometry = new THREE.PlaneGeometry(size, size, 64, 64);

    const material = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        layer: { value: i },
        opacity: { value: 0.3 - i * 0.03 },
      },
      vertexShader: `
        varying vec2 vUv;
        varying vec3 vPosition;
        uniform float time;
        uniform float layer;
        
        float noise(vec2 p) {
          return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
        }
        
        float smoothNoise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          float a = noise(i);
          float b = noise(i + vec2(1.0, 0.0));
          float c = noise(i + vec2(0.0, 1.0));
          float d = noise(i + vec2(1.0, 1.0));
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
        }
        
        void main() {
          vUv = uv;
          vPosition = position;
          
          vec3 pos = position;
          
          float wave = smoothNoise(uv * 3.0 + time * 0.1 + layer * 0.5) * 10.0;
          pos.z += wave;
          
          gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
        }
      `,
      fragmentShader: `
        varying vec2 vUv;
        varying vec3 vPosition;
        uniform float time;
        uniform float layer;
        uniform float opacity;
        
        float random(vec2 st) {
          return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
        }
        
        float noise(vec2 st) {
          vec2 i = floor(st);
          vec2 f = fract(st);
          float a = random(i);
          float b = random(i + vec2(1.0, 0.0));
          float c = random(i + vec2(0.0, 1.0));
          float d = random(i + vec2(1.0, 1.0));
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
        }
        
        float fbm(vec2 st) {
          float value = 0.0;
          float amplitude = 0.5;
          float frequency = 0.0;
          for (int i = 0; i < 6; i++) {
            value += amplitude * noise(st);
            st *= 2.0;
            amplitude *= 0.5;
          }
          return value;
        }
        
        void main() {
          vec2 uv = vUv - 0.5;
          float dist = length(uv);
          
          float angle = atan(uv.y, uv.x);
          float radius = dist;
          angle += time * 0.1 + radius * 2.0 + layer * 0.3;
          
          vec2 swirlUv = vec2(cos(angle) * radius, sin(angle) * radius) + 0.5;
          
          float n1 = fbm(swirlUv * 4.0 + time * 0.05);
          float n2 = fbm(swirlUv * 8.0 - time * 0.03);
          float n3 = fbm(swirlUv * 16.0 + time * 0.02);
          
          float nebula = n1 * 0.5 + n2 * 0.3 + n3 * 0.2;
          
          float diskMask = 1.0 - smoothstep(0.2, 0.8, dist);
          nebula *= diskMask;
          
          vec3 color;
          if (dist < 0.2) {
            color = mix(vec3(1.0, 1.0, 0.8), vec3(1.0, 0.9, 0.6), nebula);
          } else if (dist < 0.4) {
            color = mix(vec3(1.0, 0.7, 0.4), vec3(0.9, 0.5, 1.0), nebula);
          } else {
            color = mix(vec3(0.7, 0.3, 1.0), vec3(0.4, 0.2, 0.8), nebula);
          }
          
          color *= (0.8 + nebula * 0.4);
          
          float alpha = nebula * opacity * diskMask;
          gl_FragColor = vec4(color, alpha);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    const nebulaMesh = new THREE.Mesh(geometry, material);
    nebulaMesh.position.z = -200 - i * 50;
    nebulaMesh.rotation.z = (i * Math.PI) / 4;
    nebulaMesh.userData = {
      rotationSpeed: 0.001 + i * 0.0005,
      layer: i,
    };

    spaceNebulaGroup.add(nebulaMesh);
  }

  const coreGeometry = new THREE.SphereGeometry(25, 32, 32);
  const coreMaterial = new THREE.ShaderMaterial({
    uniforms: {
      time: { value: 0 },
    },
    vertexShader: `
      varying vec3 vPosition;
      uniform float time;
      
      void main() {
        vPosition = position;
        vec3 pos = position;
        pos *= 1.0 + sin(time * 3.0) * 0.1;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
      }
    `,
    fragmentShader: `
      varying vec3 vPosition;
      uniform float time;
      
      void main() {
        float dist = length(vPosition) / 25.0;
        float alpha = 1.0 - smoothstep(0.0, 1.0, dist);
        
        vec3 color = vec3(1.0, 1.0, 0.9);
        float pulse = sin(time * 2.0) * 0.2 + 0.8;
        color *= pulse;
        
        gl_FragColor = vec4(color, alpha * 0.8);
      }
    `,
    transparent: true,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
  });

  const core = new THREE.Mesh(coreGeometry, coreMaterial);
  core.position.z = -200;
  spaceNebulaGroup.add(core);

  spaceScene.add(spaceNebulaGroup);
}

function setupSpaceEventListeners() {
  window.addEventListener("resize", onSpaceWindowResize, false);

  // Setup warp slider control
  const warpSlider = document.getElementById("warp-slider");
  if (warpSlider) {
    warpSlider.addEventListener("input", (event) => {
      spaceWarpFactor = parseFloat(event.target.value);
      spaceManualWarpControl = true; // User is manually controlling warp

      // Add visual indicator for manual control
      const warpControls = document.getElementById("warp-controls");
      if (warpControls) {
        warpControls.classList.add("manual-control");
      }

      // Reset camera shaking if warp is high
      if (spaceWarpFactor > 0.8) {
        spaceCameraShaking = true;
        spaceShakeEndTime = spaceClock.getElapsedTime() + spaceShakeDuration;
      } else {
        spaceCameraShaking = false;
      }
    });

    warpSlider.addEventListener("change", (event) => {
      // Additional handling for when user releases the slider
      const newWarpFactor = parseFloat(event.target.value);
      spaceWarpFactor = newWarpFactor;
      spaceManualWarpControl = true;
    });

    // Double-click to reset to automatic mode
    warpSlider.addEventListener("dblclick", () => {
      spaceManualWarpControl = false;
      spaceWarpFactor = 0.01;
      warpSlider.value = 0.01;

      // Remove manual control visual indicator
      const warpControls = document.getElementById("warp-controls");
      if (warpControls) {
        warpControls.classList.remove("manual-control");
      }

      console.log("Automatic warp progression restored");
    });
  }
}

function onSpaceWindowResize() {
  if (spaceCamera && spaceRenderer) {
    spaceCamera.aspect = window.innerWidth / window.innerHeight;
    spaceCamera.updateProjectionMatrix();
    spaceRenderer.setSize(window.innerWidth, window.innerHeight);
  }
}

function animateSpace() {
  spaceAnimationFrame = requestAnimationFrame(animateSpace);

  // Performance monitoring
  performanceManager.updatePerformanceStats(performance.now());

  const delta = spaceClock.getDelta();
  const elapsedTime = spaceClock.getElapsedTime();

  // Only do automatic warp progression if user hasn't manually controlled it
  if (!spaceManualWarpControl) {
    const warpStartTime = 2.0;
    const warpDuration = 380.0;
    const maxWarp = 1.0;

    if (elapsedTime > warpStartTime) {
      const timeSinceWarpStart = elapsedTime - warpStartTime;
      if (timeSinceWarpStart < warpDuration) {
        const progress = timeSinceWarpStart / warpDuration;
        const exponentialFactor = 3.0;
        spaceWarpFactor = Math.pow(progress, exponentialFactor) * maxWarp;
        const warpSlider = document.getElementById("warp-slider");
        if (warpSlider) {
          warpSlider.value = spaceWarpFactor;
        }
      } else {
        if (spaceWarpFactor < maxWarp) {
          spaceWarpFactor = maxWarp;
          spaceCameraShaking = true;
          spaceShakeEndTime = elapsedTime + spaceShakeDuration;
        }
      }
    }
  }

  if (spaceHeader) {
    const headerTitle = spaceHeader.querySelector("h1");
    const headerSubtitle = spaceHeader.querySelector("p");

    const skew = Math.sin(elapsedTime * 0.3) * -1.5;
    const blur = Math.abs(Math.cos(elapsedTime * 0.3)) * 0.75;

    if (headerTitle) {
      headerTitle.style.transform = `skewX(${skew}deg)`;
      headerTitle.style.filter = `blur(${blur}px)`;
    }
    if (headerSubtitle) {
      headerSubtitle.style.transform = `skewX(${skew}deg)`;
      headerSubtitle.style.filter = `blur(${blur}px)`;
    }
  }

  if (spaceStars) {
    spaceStars.material.uniforms.time.value = elapsedTime;
    spaceStars.material.uniforms.warpFactor.value = spaceWarpFactor;
  }

  if (spaceCameraShaking) {
    if (elapsedTime < spaceShakeEndTime) {
      const shakeIntensity = 1.5;
      spaceCamera.position.x = (Math.random() - 0.5) * shakeIntensity;
      spaceCamera.position.y = (Math.random() - 0.5) * shakeIntensity;
    } else {
      spaceCameraShaking = false;
      spaceCamera.position.x = 0;
      spaceCamera.position.y = 0;
    }
  }

  if (spaceNebulaGroup) {
    spaceNebulaGroup.rotation.y += delta * 0.02;

    spaceNebulaGroup.children.forEach((child) => {
      if (child.material && child.material.uniforms) {
        child.material.uniforms.time.value = elapsedTime;
      }

      if (child.userData && child.userData.rotationSpeed) {
        child.rotation.z += child.userData.rotationSpeed;
      }
    });
  }

  if (spaceRenderer && spaceScene && spaceCamera) {
    spaceRenderer.render(spaceScene, spaceCamera);
  }
}

async function cleanupSpaceBackground() {
  if (spaceScene) {
    // Dispose of all geometries and materials
    spaceScene.traverse((child) => {
      if (child.geometry) {
        child.geometry.dispose();
      }
      if (child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach((material) => material.dispose());
        } else {
          child.material.dispose();
        }
      }
    });
    spaceScene.clear();
  }
  if (spaceRenderer) {
    spaceRenderer.dispose();
  }
}

// Make Three.js available globally for space background
import * as THREE from "three";
window.THREE = THREE;

// Auto-initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  initBackgroundManager();
});

export { initBackgroundManager };
